import { useMutation, useLazyQuery } from "@apollo/client/react";
import { CREATE_OR_UPDATE_ORDEN_PEDIDO } from "../../../data/graphQL/mutations/GestionAsistencial/createOrUpdateOrdenPedido";
import toast from "react-hot-toast";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { ENVIO_ESTADO_AUTORIZAR_ID, ENVIO_ESTADO_CANCELADO_ID, ENVIO_ESTADO_AUTORIZADO_ID } from "./useEnvioCreationContentInputs";
import { CREATE_JUSTIFICACION_CONDICIONAL_PEDIDO } from "../../../data/graphQL/mutations/GestionAsistencial/createJustificacionCondicionalPedido";
import { VALIDAR_PEDIDO_CONEXION } from "../../../data/graphQL/mutations/GestionAsistencial/validarOrdenPedido";

import { ID_RESULTADO_CONDICIONAL } from "../components/EnvioCreationContent";

export const ESTADO_JUSTIFICACION_APROBADO_ID = 2;


export function useCreateOrUpdateOrdenPedido({
  idOrdenPedido = 0,
  idEvaluacion,
  formValues,
  insumosRows,
  isActualizarStockRequired,
  datosDetalle,
  showBum,
  showJustificacionCondicional,
  bloqueoClienteSAP,
  initialEnvioEstado
}) {
  const INDEX_TAB_ENVIOS = 4;
  const navigate = useNavigate();

  const [createOrUpdateOrdenPedido, { loading }] = useMutation(
    CREATE_OR_UPDATE_ORDEN_PEDIDO
  );

  const [createJustificacionCondicionalPedido, { loading: loadingJustificaicon }] = useMutation(
    CREATE_JUSTIFICACION_CONDICIONAL_PEDIDO
  );

  const [validarOrdenPedido] = useLazyQuery(VALIDAR_PEDIDO_CONEXION, {
    fetchPolicy: 'no-cache'
  });

  const validarFechaEnvio = () => {
    let errorMessage = "";
    if (!formValues.fechaEntrega) return errorMessage += "La fecha de envío es requerida. \n";

    const fechaEntrega = moment(formValues.fechaEntrega);
    const fechaDesde = moment(formValues.fechaDesdeEvaluacion);
    const fechaHasta = moment(formValues.fechaHastaEvaluacion);

    if (!fechaEntrega.isBetween(fechaDesde, fechaHasta, 'day', '[]')) {
      errorMessage += "La fecha de envío debe estar dentro del rango de fechas de la evaluación. \n";
    }

    if (!fechaEntrega.isSameOrAfter(moment().startOf("day"))) {
      errorMessage += "La fecha de envío no puede ser en el pasado. \n";
    }

    return errorMessage;
  };

  const validarJustificacionesCondicionales = () => {
    let errorMessage = "";
    if (formValues.idEstadoEnvio !== ENVIO_ESTADO_AUTORIZADO_ID || !showJustificacionCondicional) return errorMessage;

    if (formValues.idEstadoComercial !== ESTADO_JUSTIFICACION_APROBADO_ID ||
      formValues.idEstadoMedico !== ESTADO_JUSTIFICACION_APROBADO_ID ||
      (showBum && formValues.idEstadoBUM !== ESTADO_JUSTIFICACION_APROBADO_ID)) {
      errorMessage += "Las justificaciones condicionales deben estar aprobadas. \n";
    }

    return errorMessage;
  };


  const isFormInvalid = async () => {
    let errorMessage = "";
    const tieneCantidadAPedir = insumosRows.some(
      ({ cantidadAPedir }) => Number(cantidadAPedir) > 0
    );

    const tieneCantidadAPedirNegativo = insumosRows.some(
      ({ cantidadAPedir }) => Number(cantidadAPedir) < 0
    );

    if (formValues.idEstadoEnvio === ENVIO_ESTADO_AUTORIZAR_ID) {
      await validarOrdenPedido({
        variables: {
          idEvaluacion: idEvaluacion,
          fechaEnvio: formValues.fechaEntrega
        }
      }).
        then((data) => {
          if (!data?.data?.validarPedidoConexion) {
            errorMessage += "No se puede realizar el envio para la fecha seleccionada porque no tiene conexiones en el mes.\n";
          }
        }).catch(() => toast.error('Ha ocurrido un error al validar el pedido'));;
    }

    if (formValues.idEstadoEnvio !== ENVIO_ESTADO_CANCELADO_ID) {

      const tieneCantidadAPedirMayorQueStock = insumosRows.some(
        ({ cantidadAPedir, stockDisponibleDeposito }) => Number(cantidadAPedir) > Number(stockDisponibleDeposito)
      );

      if (tieneCantidadAPedirMayorQueStock)
        errorMessage += "Al menos un insumo tiene más cantidad a pedir que el stock disponible en depósito.\n";

      errorMessage += validarJustificacionesCondicionales();

      // cabecera
      if (!formValues.idEstadoEnvio) errorMessage += "El estado es requerido. \n";
      if (formValues.idEstadoEnvio === ENVIO_ESTADO_AUTORIZADO_ID && bloqueoClienteSAP) errorMessage += "El cliente SAP está bloqueado. \n";
      errorMessage += validarFechaEnvio();
      if (!formValues.idDeposito) errorMessage += "El pedido de déposito es requerido. \n";
      if (!(formValues.diasPedido > 0)) errorMessage += "El pedido para los próx. días debe ser mayor a 0. \n";
      // direccion
      if (!formValues.direccion) errorMessage += "La dirección es requerida. \n";
      if (!formValues.provincia) errorMessage += "La provincia es requerida. \n";
      if (!formValues.localidad) errorMessage += "La localidad es requerida. \n";
      if (!formValues.codigoPostal) errorMessage += "El código postal es requerido. \n";
      if (!formValues.pais) errorMessage += "El país es requerido. \n";
      // insumos
      if (!tieneCantidadAPedir) errorMessage += "Al menos un insumo debe tener la cantidad a pedir mayor a 0. \n";
      if (tieneCantidadAPedirNegativo) errorMessage += "La cantidad a pedir de los insumos no puede ser un valor negativo. \n";
      if (initialEnvioEstado !== ENVIO_ESTADO_AUTORIZADO_ID && isActualizarStockRequired) errorMessage += "Se requiere actualizar el stock. \n";
    }

    if (errorMessage) {
      toast.error(errorMessage);
    }

    return errorMessage;
  };

  const handleCreateOrUpdateOrdenPedido = async () => {
    if (await isFormInvalid()) return;

    const ordenPedidoInsumo = insumosRows.map(
      ({
        cantidadCuota,
        cantidadPlan,
        cantidadAPedir,
        cantidadDiaria,
        descripcion,
        saldo,
        stockDisponibleDeposito,
        cuotaActiva,
        cod_Articulo,
        idInsumo,
        idInsumoN,
        por,
        ...rest
      }) => ({
        aPedir: +cantidadAPedir,
        cantidad: parseInt(cantidadPlan, 10),
        cantidadOriginal: +cantidadDiaria,
        descripcionInsumo: descripcion,
        idInsumo,
        idInsumoN,
        por
      })
    );

    const variables = {
      // cabecera
      idOrdenPedido,
      idDelegacion: formValues.idDelegacion,
      idEvaluacion,
      fechaEntrega: moment(formValues.fechaEntrega).format("YYYY-MM-DD"),
      idEstado: formValues.idEstadoEnvio,
      diasPedido: formValues.diasPedido,
      idStockOrigen: formValues.idDeposito,
      complejidadPlan: formValues.complejidadPlan,
      idResultadoPresupuesto: formValues.idResultadoPresupuesto,
      diasCO: formValues.diasCO,
      diasFacturacion: formValues.diasFacturacion,

      // direccion
      calle: formValues.direccion,
      cP: formValues.codigoPostal,
      localidad: formValues.localidad,
      observacion: formValues.observaciones,
      pais: "AR", // siempre es AR
      provincia: formValues.provincia,
      telefono: formValues.telefono,
      // insumos
      ordenPedidoInsumo
    };

    await createOrUpdateOrdenPedido({
      variables,
      onCompleted: (data) => {
        const { success, valorEsperado } = data?.createOrUpdateOrdenPedido || {};
        if (!success) {
          toast.error(valorEsperado);
        } else {

          if (
            formValues.idEstadoEnvio === ENVIO_ESTADO_AUTORIZAR_ID &&
            formValues.complejidadPlan &&
            formValues.idResultadoPresupuesto === ID_RESULTADO_CONDICIONAL) {

            const estadoPEnvio = formValues.diasCO > formValues.diasFacturacion * 0.25;
            createJustificacionCondicionalPedido({
              variables: {
                idEvaluacion,
                idOrdenPedido,
                estadoPEnvio,
                diasCondicional: formValues.diasCO,
                diasMaxFacturacionCliente: formValues.diasFacturacion,
                contratosCO: formValues.contratosCo
              }
            });

          }

          toast.success(valorEsperado);

          if (datosDetalle.fromScreen) {
            navigate("/asistencial/gestionEnvios", {
              state: {
                activeTab: datosDetalle?.activeTab
              }
            });
          } else {
            navigate("/asistencial/gestionAsistencial/detalle", {
              state: {
                ...datosDetalle,
                activeTab: INDEX_TAB_ENVIOS
              }
            });
          }
        }
      }
    });
  };

  return {
    handleCreateOrUpdateOrdenPedido, loading: loading || loadingJustificaicon
  };
}